import { useState, useEffect, useCallback } from 'react';
import { enhancedApi } from '../../../shared/services/enhancedApi';
import { useAppStore } from '@/store/useAppStore';
import type { Destination } from '@/shared/types';

export interface Destinations {
  destinationId: number;
  destinationID: string;
  destinationName: string;
  destinationCode: string;
  areaType: string;
  areaCoverage: string[];
}

interface UseDestinationReturn {
  destinations: Destinations[];
  destinationLocationOptions: string[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Map section types to API scope values
const getScopeFromSection = (section?: string): string => {
  switch (section) {
    case 'Domestic':
      return 'local';
    case 'Asia and Oceania':
      return 'asiaOceania';
    case 'WorldwideElite':
      return 'schengen';
    case 'ShortSecure':
      return 'local'; // Assuming ShortSecure uses local destinations
    default:
      return 'local'; // Default fallback
  }
};

export const useDestinations = (section?: string): UseDestinationReturn => {
  const { setAllLocalDestinations } = useAppStore();

  const [destinations, setDestinations] = useState<Destinations[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDestinations = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const scope = getScopeFromSection(section);
      
      // Use enhanced API with caching and queuing
      const response = await enhancedApi.cachedGet<{
        success: boolean;
        destinations: Destinations[];
      }>(
        `/api/destinations/by-scope?scope=${scope}`,
        {},
        15 * 60 * 1000 // 15 minutes TTL - destinations are semi-static
      );
      
      if (response.success && response.destinations) {
        // sort A → Z by destinationName
        const sortedDestinations = [...response.destinations].sort((a, b) =>
          a.destinationName.localeCompare(b.destinationName)
        );

        console.log(`Fetched destinations for ${section} (${scope}) - cached/sorted:`, sortedDestinations);

        setAllLocalDestinations(
          sortedDestinations.map(
            (country): Destination => ({
              destinationId: country.destinationId,
              destinationName: country.destinationName,
              destinationCode: country.destinationCode,
              areaType: country.areaType,
              areaCoverage: country.areaCoverage,
            })
          )
        );

        setDestinations(sortedDestinations);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error(`Error fetching destinations for ${section}:`, err);
      setError(`Failed to fetch destinations for ${section || 'selected section'}`);
    } finally {
      setLoading(false);
    }
  }, [section]); // Add section as dependency

  useEffect(() => {
    fetchDestinations();
  }, [fetchDestinations]);

  // Convert destinations to location options format for destination dropdowns
  const destinationLocationOptions = destinations.map(destination => destination.destinationName);

  return {
    destinations,
    destinationLocationOptions,
    loading,
    error,
    refetch: fetchDestinations
  };
};